/**
 * Utility for parsing Person Block related query parameters from the URL.
 *
 * Supported query parameters:
 * - firstName: First name
 * - lastName: Last name
 * - email: Email address
 * - homePhone: Home phone number
 * - mobilePhone: Mobile phone number
 * - workPhone: Work phone number
 * - phone: Generic phone (used for custom phone type)
 * - address: Street address line 1
 * - address2: Street address line 2
 * - city: City
 * - state / region: State or region
 * - zip / postalCode: ZIP or postal code
 * - country: Country name
 * - dob / dateOfBirth: Date of birth (format: YYYY-MM-DD)
 * - birthday: Birthday without year (format: MM-DD)
 * - relationship: Relationship field value
 * - amount: Payment amount (triggers auto-checkout)
 * - donationName: Custom name for the donation/amount (default: "Donation")
 * - skipCheckout: Skip the checkout page and go straight to payment (default: false)
 *
 * Example URL:
 * /venue/form?firstName=John&lastName=Doe&email=john@example.com&amount=100
 * /venue/form?firstName=John&lastName=Doe&email=john@example.com&amount=50&skipCheckout=true
 */

/**
 * Parse person-related query parameters from the current URL
 * @returns {Object} Object containing parsed person field values
 */
export function parsePersonQueryParams() {
    const urlParams = new URLSearchParams(window.location.search);

    const personParams = {
        firstName: urlParams.get('firstName'),
        lastName: urlParams.get('lastName'),
        email: urlParams.get('email'),
        homePhone: urlParams.get('homePhone'),
        mobilePhone: urlParams.get('mobilePhone'),
        workPhone: urlParams.get('workPhone'),
        customPhone: urlParams.get('phone'),
        address: urlParams.get('address'),
        address2: urlParams.get('address2'),
        city: urlParams.get('city'),
        state: urlParams.get('state') || urlParams.get('region'),
        zip: urlParams.get('zip') || urlParams.get('postalCode'),
        country: urlParams.get('country'),
        dob: urlParams.get('dob') || urlParams.get('dateOfBirth'),
        birthday: urlParams.get('birthday'),
        relationship: urlParams.get('relationship'),
    };

    // Filter out null/undefined values
    const filteredParams = {};
    Object.keys(personParams).forEach(key => {
        if (personParams[key] !== null && personParams[key] !== undefined) {
            filteredParams[key] = personParams[key];
        }
    });

    return filteredParams;
}

/**
 * Get the form field name for a person block field
 * @param {string} fieldType - The field type (e.g., 'first-name', 'email')
 * @param {string} blockId - The person block ID
 * @returns {string} The full field name
 */
export function getPersonBlockFieldName(fieldType, blockId) {
    return `person-block-${fieldType}-${blockId}`;
}

/**
 * Map query param keys to person block field type suffixes
 */
export const QUERY_PARAM_TO_FIELD_MAP = {
    firstName: 'first-name',
    lastName: 'last-name',
    email: 'email',
    homePhone: 'home-phone',
    mobilePhone: 'mobile-phone',
    workPhone: 'work-phone',
    customPhone: 'custom-phone',
    address: 'address',
    address2: 'address-2',
    city: 'city',
    zip: 'zip',
    country: 'country',
    dob: 'dob',
    birthday: 'birthday',
    relationship: 'relationship',
};

/**
 * Check if any person-related query params are present
 * @returns {boolean}
 */
export function hasPersonQueryParams() {
    return Object.keys(parsePersonQueryParams()).length > 0;
}

/**
 * Parse checkout-related query parameters (amount, donation name)
 * @returns {Object} Object containing checkout params
 */
export function parseCheckoutQueryParams() {
    const urlParams = new URLSearchParams(window.location.search);

    const amountStr = urlParams.get('amount');
    const amount = amountStr ? parseFloat(amountStr) : null;

    return {
        amount: (amount && !isNaN(amount) && amount > 0) ? amount : null,
        donationName: urlParams.get('donationName') || 'Donation',
        autoCheckout: urlParams.get('autoCheckout') !== 'false', // Default to true if amount is present
        skipCheckout: urlParams.get('skipCheckout') === 'true', // Skip checkout page and go straight to payment
    };
}

/**
 * Check if auto-checkout should be triggered
 * @returns {boolean}
 */
export function shouldAutoCheckout() {
    const { amount, autoCheckout } = parseCheckoutQueryParams();
    return amount !== null && autoCheckout;
}
